import { FC, useRef } from 'react'

import styled from 'styled-components'
import { Controller, FieldError, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import ReCAPTCHA from 'react-google-recaptcha'
import { useNavigate } from 'react-router-dom'
import { CountryCode } from 'libphonenumber-js'

import { PrimaryInput } from 'src/Components/PrimaryInput'
import { PrimaryPhoneInput } from 'src/Components/PrimaryPhoneInput'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { GoogleAuthButton } from 'src/Components/Buttons/GoogleAuthButton'
import { TextLinkButton } from 'src/Components/Buttons/TextLinkButton'
import { OrDivider } from 'src/Components/OrDivider'
import { EmailExist } from 'src/Pages/Artist/CreateCampaignPage/NoAuthPages/ArtistRegisterPage/Components/ArtistRegisterForm/Components/EmailExist'
import { APP } from 'src/Configs/App'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import { useQuickLoginUTMParameters } from 'src/Hooks/useQuickLoginUTMParameters'
import {
  signupErrorToLogin,
  signupToLogin,
} from 'src/Helpers/TagManager/AuthEvents'
import { login } from 'src/Router/routes'
import { useAppSelector } from 'src/Hooks/redux'
import { getCountry } from 'src/Redux/auth-process/countrySlice/selectors'

import { contactDetailsSchema } from './schema'

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    width: 100%;
  }
`

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  width: 100%;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`

const StyledCTAButton = styled(CTAButton)`
  padding: 0 48px;
`

const Footer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`

const FooterText = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

const CaptchaWrapper = styled.div`
  position: absolute;
`

const ErrorRow = styled.div`
  color: ${({ theme }) => theme.color.mediumRed};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

export interface FormDataProps {
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string | null
}

interface ArtistContactDetailsFormProps {
  onSubmit: (data: FormDataProps, token: string) => Promise<void>
  onGoogleClick: () => void
  isEmailExist: boolean
  clearEmailExist: () => void
  onStartFillingForm: () => void
  loading?: boolean
  googleAuthError?: string | null
  className?: string
}

const getDisplayError = (
  value: unknown,
  error: FieldError | undefined,
  submitCount: number,
): string | undefined => {
  if (!error) {
    return void 0
  }
  if (value) {
    return error.message
  }
  return submitCount > 0 ? error.message : void 0
}

export const ArtistContactDetailsForm: FC<ArtistContactDetailsFormProps> = ({
  onSubmit,
  onGoogleClick,
  isEmailExist,
  clearEmailExist,
  onStartFillingForm,
  loading,
  googleAuthError,
  className,
}) => {
  const captchaRef = useRef<ReCAPTCHA>(null)
  const navigate = useNavigate()
  const utmParameters = useQuickLoginUTMParameters()
  const countryCode = useAppSelector(getCountry)

  const {
    handleSubmit,
    control,
    setError,
    clearErrors,
    formState: { submitCount },
  } = useForm<FormDataProps>({
    mode: 'onBlur',
    resolver: yupResolver(contactDetailsSchema),
  })

  const onSubmitForm = handleSubmit(async (data) => {
    try {
      const token = await captchaRef.current?.executeAsync()
      if (!token) {
        return
      }

      await onSubmit(data, token)
    } finally {
      captchaRef.current?.reset()
    }
  })

  const params = new URLSearchParams(utmParameters).toString()
  const loginWithParams = `${login}?${params}`

  return (
    <FormColumn as='form' className={className} onSubmit={onSubmitForm}>
      <GoogleAuthButton label='Continue with Google' onClick={onGoogleClick} />
      {googleAuthError && <ErrorRow>{googleAuthError}</ErrorRow>}
      <OrDivider />
      <InputsContainer>
        <Controller
          name='firstName'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PrimaryInput
              {...field}
              placeholder='First name'
              error={getDisplayError(field.value, error, submitCount)}
              autoComplete='given-name'
              onFocus={onStartFillingForm}
            />
          )}
        />
        <Controller
          name='lastName'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PrimaryInput
              {...field}
              placeholder='Last name'
              error={getDisplayError(field.value, error, submitCount)}
              autoComplete='family-name'
            />
          )}
        />
        <>
          <Controller
            name='email'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PrimaryInput
                {...field}
                placeholder='Email'
                error={getDisplayError(field.value, error, submitCount)}
                autoComplete='email'
                onChange={(e) => {
                  field.onChange(e)
                  clearEmailExist()
                }}
              />
            )}
          />
          <EmailExist emailExist={isEmailExist} />
        </>
        <Controller
          control={control}
          name='phoneNumber'
          render={({ field, fieldState: { error } }) => (
            <PrimaryPhoneInput
              name={field.name}
              onChange={field.onChange}
              country={countryCode as CountryCode}
              value={field.value ?? ''}
              error={error?.message}
              autoComplete='tel'
              onValidationError={(validationError) => {
                if (validationError) {
                  setError('phoneNumber', { message: validationError })
                } else {
                  clearErrors('phoneNumber')
                }
              }}
            />
          )}
        />
      </InputsContainer>
      <CaptchaWrapper>
        <ReCAPTCHA
          ref={captchaRef}
          size='invisible'
          sitekey={APP.RE_CAPTCHA_SITE_KEY}
          badge='bottomright'
        />
      </CaptchaWrapper>
      <ButtonsContainer>
        <StyledCTAButton type='submit' loading={loading}>
          Continue
        </StyledCTAButton>
        <Footer>
          <FooterText>Already have an account?</FooterText>
          <TextLinkButton
            type='button'
            onClick={() => {
              if (isEmailExist) {
                signupErrorToLogin()
              } else {
                signupToLogin()
              }
              navigate(loginWithParams)
            }}
          >
            Log in
          </TextLinkButton>
        </Footer>
      </ButtonsContainer>
    </FormColumn>
  )
}
